/**
 * Inventory Items API client helpers
 *
 * Plain async functions backed by directApiClient. Used by React Query hooks
 * (mutationFn / queryFn) and by non-component callers (data loaders, AI
 * ingestion). Throws on error — callers handle via React Query / try-catch.
 *
 * Server actions for this service were retired in LF-3017; cache invalidation
 * happens through `getDependencies('inventory-item', matterId)` at the hook
 * level (see hooks.tsx) instead of `revalidatePath`.
 */

import { directApiClient } from '@/lib/api/direct-client';
import {
  CreateInventoryItemRequest,
  CreateInventoryItemRequestSchema,
  InventoryItem,
  InventoryItemFilters,
  InventoryItemFiltersSchema,
  InventoryItemsListResponse,
  SetValueConfirmationRequest,
  UpdateInventoryItemRequest,
  UpdateInventoryItemRequestSchema,
  ValueConfirmationResponse,
  ValueConfirmationSourcesResponse,
  ReclassificationPreview
} from './types';
import { buildInventoryListResponse } from './utils';

class InventoryValidationError extends Error {
  fieldErrors: Record<string, string[] | undefined>;
  constructor(
    message: string,
    fieldErrors: Record<string, string[] | undefined>
  ) {
    super(message);
    this.name = 'InventoryValidationError';
    this.fieldErrors = fieldErrors;
  }
}

export async function fetchInventoryItems(
  matterId: number,
  filters?: InventoryItemFilters
): Promise<InventoryItemsListResponse> {
  if (filters) {
    const result = InventoryItemFiltersSchema.safeParse(filters);
    if (!result.success) {
      throw new InventoryValidationError(
        'Invalid filters',
        result.error.flatten().fieldErrors
      );
    }
  }

  const items = await directApiClient.get<InventoryItem[]>(
    `/matters/${matterId}/inventory-items`,
    { params: filters as Record<string, unknown> | undefined }
  );
  return buildInventoryListResponse(items || []);
}

export async function createInventoryItem(
  matterId: number,
  data: CreateInventoryItemRequest
): Promise<InventoryItem> {
  const validation = CreateInventoryItemRequestSchema.safeParse(data);
  if (!validation.success) {
    throw new InventoryValidationError(
      'Invalid inventory item',
      validation.error.flatten().fieldErrors
    );
  }

  return directApiClient.post<InventoryItem>(
    `/matters/${matterId}/inventory-items`,
    validation.data
  );
}

export async function updateInventoryItem(
  matterId: number,
  inventoryItemId: number,
  data: UpdateInventoryItemRequest
): Promise<InventoryItem> {
  // Backend returns type as object; schema expects slug string
  const normalized =
    data.type && typeof data.type === 'object' && 'slug' in data.type
      ? { ...data, type: (data.type as { slug: string }).slug }
      : data;

  const validation = UpdateInventoryItemRequestSchema.safeParse(normalized);
  if (!validation.success) {
    throw new InventoryValidationError(
      'Invalid inventory item update',
      validation.error.flatten().fieldErrors
    );
  }

  return directApiClient.put<InventoryItem>(
    `/matters/${matterId}/inventory-items/${inventoryItemId}`,
    validation.data
  );
}

export async function deleteInventoryItem(
  matterId: number,
  inventoryItemId: number
): Promise<void> {
  await directApiClient.delete(
    `/matters/${matterId}/inventory-items/${inventoryItemId}`
  );
}

/**
 * Confirm an item's current value against a source (LF-4356). At least one of
 * `source` / `sourceFileId` must be supplied; who + when are captured
 * server-side. Returns the derived confirmation state.
 */
export async function setValueConfirmation(
  matterId: number,
  inventoryItemId: number,
  data: SetValueConfirmationRequest
): Promise<ValueConfirmationResponse> {
  return directApiClient.patch<ValueConfirmationResponse>(
    `/matters/${matterId}/inventory-items/${inventoryItemId}/value-confirmation`,
    data
  );
}

/**
 * Clear an item's current-value confirmation (LF-4356) — reverts it to the
 * Estimate state. Idempotent.
 */
export async function clearValueConfirmation(
  matterId: number,
  inventoryItemId: number
): Promise<ValueConfirmationResponse> {
  return directApiClient.delete<ValueConfirmationResponse>(
    `/matters/${matterId}/inventory-items/${inventoryItemId}/value-confirmation`
  );
}

/**
 * List institution-response attachments the manual value-confirmation picker can
 * cite as a source for this item (LF-5393). Scoped to the item's institution;
 * empty when the item has no institution or no responses carry attachments.
 */
export async function fetchValueConfirmationSources(
  matterId: number,
  inventoryItemId: number
): Promise<ValueConfirmationSourcesResponse> {
  return directApiClient.get<ValueConfirmationSourcesResponse>(
    `/matters/${matterId}/inventory-items/${inventoryItemId}/value-confirmation-sources`
  );
}

/**
 * What changing this item's type would do, without doing it (VALE-1387).
 *
 * Read-only. Runs the same rule the subsequent `updateInventoryItem` runs, so the
 * consequences shown to the reader are the ones that will actually apply.
 */
export async function previewInventoryItemReclassification(
  matterId: number,
  inventoryItemId: number,
  toType: string
): Promise<ReclassificationPreview> {
  return directApiClient.get<ReclassificationPreview>(
    `/matters/${matterId}/inventory-items/${inventoryItemId}/reclassification-preview?toType=${encodeURIComponent(toType)}`
  );
}
